import { useMemo, useState } from 'react';
import { track } from '@gonfalon/analytics/track';
import { useAccountContext } from '@gonfalon/app-context/useAccountContext';
import { useDebounce } from '@gonfalon/async/useDebounce';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Group, IconButton, Input, SearchField } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import { type AggregationType, TimeRangeOption, UsageTab, type UsageTabKey } from '../../../types';
import { usageTabConfig } from '../../constants/usageTabConfig';
import { useSuspenseUsageData } from '../../hooks/useSuspenseUsageData';
import { useUsageDetailSearchParams } from '../../hooks/useUsageDetailSearchParams';
import { useUsageMonthOptions } from '../../hooks/useUsageMonthOptions';
import { getGranularity } from '../../utils/getGranularity';
import { getSubscriptionLimits } from '../../utils/getSubscriptionLimits';
import { getUsageDisplayName } from '../../utils/getUsageDisplayName';
import { getMaxYValue, UsageDetailChart } from '../charts/UsageDetailChart';

import { UsageDetailAlert, UsageDetailControls, UsageDetailGrouped, UsageSummaryCards } from './components';
import { DataExportCombinedDetail } from './DataExportCombinedDetail';

import styles from './UsageDetail.module.css';

const DEBOUNCE_MS = 200;

type UsageDetailProps = {
  usageTab: UsageTabKey;
  defaultAggregationType: AggregationType;
  showTrialEnd?: boolean;
};

type DefaultSearchParams = {
  timeRange: TimeRangeOption;
  month: string;
  aggregationType: AggregationType;
};

export function UsageDetail(props: UsageDetailProps) {
  if (props.usageTab === UsageTab.DATA_EXPORT_COMBINED) {
    return (
      <DataExportCombinedDetail
        defaultAggregationType={props.defaultAggregationType}
        showTrialEnd={props.showTrialEnd}
      />
    );
  }
  return <SingleQueryUsageDetail {...props} />;
}

function SingleQueryUsageDetail({ usageTab, defaultAggregationType, showTrialEnd = false }: UsageDetailProps) {
  const { subscription } = useAccountContext();
  const [inputValue, setInputValue] = useState('');
  const [search, setSearch] = useState('');
  const [hasGroupedResults, setHasGroupedResults] = useState(false);

  const { today, monthOptions, defaultMonth } = useUsageMonthOptions();

  const defaultParams: DefaultSearchParams = {
    timeRange: TimeRangeOption.MONTH_TO_DATE,
    month: defaultMonth,
    aggregationType: defaultAggregationType,
  };
  const [params, setParams] = useUsageDetailSearchParams(usageTab, defaultParams);

  const debouncedSetSearch = useDebounce((value: string) => {
    setSearch(value);
    if (value.trim()) {
      track('Filtered Dimensions By Text', 'Plan Usage', {
        dimension: params.dimension,
        usageTab: usageTab.toString(),
        aggregationType: params.aggregationType,
      });
    }
  }, DEBOUNCE_MS);

  const onChange = (value: string) => {
    setInputValue(value);
    debouncedSetSearch(value);
  };

  const clearSearch = () => {
    setInputValue('');
    setSearch('');
  };

  const searchResetKey = `${params.timeRange}|${params.month}|${params.aggregationType}|${params.dimension ?? ''}`;
  const [prevSearchResetKey, setPrevSearchResetKey] = useState(searchResetKey);
  if (prevSearchResetKey !== searchResetKey) {
    setPrevSearchResetKey(searchResetKey);
    setInputValue('');
    setSearch('');
  }

  const granularity = getGranularity(params.timeRange);
  const {
    data: totalData,
    timestamps,
    hasProjection,
  } = useSuspenseUsageData({
    usageTab,
    timeRange: params.timeRange,
    month: params.month,
    aggregationType: params.aggregationType,
    today,
    granularity,
  });

  const { limitName, meterType, precision } = usageTabConfig[usageTab];
  // Include the projected slices in the y-axis max when a projection is drawn: the cumulative
  // month-end projection runs above the last actual point, so a max derived from actuals alone
  // would clip the dashed line at the top of the chart.
  const yAxisMax = useMemo(
    () =>
      getMaxYValue(
        hasProjection ? [...(totalData.series ?? []), ...(totalData.projectedSeries ?? [])] : (totalData.series ?? []),
        precision,
      ),
    [totalData.series, totalData.projectedSeries, hasProjection, precision],
  );
  const hasUsageData = (totalData.series ?? []).length > 0;

  const abbreviatedName = getUsageDisplayName(usageTab, 'abbreviated');
  const limits = getSubscriptionLimits(subscription);
  const limitValue = limitName ? (limits?.[limitName] ?? 0) : 0;

  const trialEndUTC = useMemo(() => {
    if (!showTrialEnd || !subscription?.trialEndDate) {
      return undefined;
    }
    return new Date(subscription.trialEndDate).getTime();
  }, [subscription, showTrialEnd]);

  return (
    <div>
      <div className={styles.container}>
        <h2>{getUsageDisplayName(usageTab, 'longTitleName')}</h2>
        <div className={styles.controlGroup}>
          <UsageDetailControls
            setParams={setParams}
            usageTab={usageTab}
            selectedTimeRange={params.timeRange}
            selectedMonth={params.month}
            monthOptions={monthOptions}
            selectedAggregationType={params.aggregationType}
            selectedDimension={params.dimension}
            granularity={granularity}
          >
            <UsageSummaryCards
              usageTab={usageTab}
              toTimestampUTC={timestamps.to}
              today={today}
              dimension={params.dimension}
              precision={precision}
              defaultAggregationType={defaultAggregationType}
            />
          </UsageDetailControls>
        </div>

        <div className={styles.chartContainer}>
          <span className={styles.chartTitle}>
            <Message id="billing.usageDetail.totalUsage" />
          </span>
          <div className={styles.chartContent}>
            <UsageDetailChart
              data={totalData}
              size={params.dimension ? 'small' : 'large'}
              condenseLabels={false}
              aggregationType={params.aggregationType}
              meterType={meterType}
              abbreviatedName={abbreviatedName}
              limitValue={limitValue}
              fromTimestampUTC={timestamps.from}
              toTimestampUTC={timestamps.to}
              cutoffTimestampUTC={timestamps.cutoff}
              yAxisMax={yAxisMax}
              granularity={granularity}
              precision={precision}
              trialEndUTC={trialEndUTC}
              showProjection={hasProjection}
            />
          </div>
        </div>

        {!!params.dimension && (
          <div>
            {hasGroupedResults && (
              <SearchField
                className={styles.searchField}
                aria-label={msg('billing.usageDetail.searchLabel')}
                value={inputValue}
                onChange={onChange}
              >
                <Group>
                  <Icon name="search" size="small" />
                  <Input placeholder={msg('billing.usageDetail.searchPlaceholder')} />
                  <IconButton
                    aria-label={msg('billing.usageDetail.clearSearchLabel')}
                    icon="cancel-circle-outline"
                    size="small"
                    variant="minimal"
                    onPress={clearSearch}
                  />
                </Group>
              </SearchField>
            )}

            <UsageDetailAlert usageTab={usageTab} dimension={params.dimension} hasUsageData={hasUsageData} />

            <UsageDetailGrouped
              usageTab={usageTab}
              aggregationType={params.aggregationType}
              timestamps={timestamps}
              today={today}
              dimension={params.dimension}
              meterType={meterType}
              abbreviatedName={abbreviatedName}
              limitValue={limitValue}
              granularity={granularity}
              search={search}
              onHasResultsChange={setHasGroupedResults}
              precision={precision}
              trialEndUTC={trialEndUTC}
            />
          </div>
        )}
      </div>
    </div>
  );
}
